// frontend/.../public/vendorPrequal/FileDropZone.tsx
// Drag-and-drop / click-to-browse file input used throughout the public
// Vendor Prequalification form. Calls the shared onFiles handler with the
// field key so the parent form can collate them into the upload map.
import { useRef, useState } from 'react';
import { TEXT } from '../../shared/Typography';

interface Props {
  fieldKey: string;
  multiple?: boolean;
  accept?: string;
  onFiles: (fieldKey: string, files: FileList | null, multiple?: boolean) => void;
}

export default function FileDropZone({
  fieldKey,
  multiple = false,
  accept,
  onFiles,
}: Props): JSX.Element {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const fl = e.dataTransfer?.files;
    if (fl && fl.length) {
      onFiles(fieldKey, fl, multiple);
    }
  }

  return (
    <div
      onDragOver={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
      }}
      onDragLeave={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
      }}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      className={`border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition ${
        isDragOver
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={e => {
          onFiles(fieldKey, e.target.files, multiple);
          // Reset so selecting the same file again re-fires onChange
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <div className={TEXT.body}>
        <strong>Drop {multiple ? 'files' : 'a file'} here</strong> or click to browse
      </div>
      {accept && (
        <div className={`mt-1 ${TEXT.caption}`}>Accepted: {accept}</div>
      )}
    </div>
  );
}
